import React, { createContext, useContext, useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { Appointment, appointmentKey, Employee, WorkHours } from '../types';
import { fetchEmployeeSchedule, fetchEmployees, getCurrentBusinessConfig, getCurrentEmployee, getEmployeeWorkHours } from '../firebase/firebase-delegate';
import { dayjs } from '../utils/dayjs-config';
import { WEBSITE_ID } from '../projectConfig';
import {
  hydrateAppointmentsIntoCache,
  refetchAppointmentIntoCache,
  refetchAppointmentIntoCacheWithStatus,
  type RefetchAppointmentResult,
} from '../utils/cache-delegate';
import {
  // Orchestrator-only helpers: keep non-orchestrator admin data flow isolated from alerting logic.
  findMissingOrchestratorAppointmentIds,
  filterNotifiableOrchestratorAppointments,
  getCachedAppointmentsByIds,
  hydrateMissingOrchestratorAppointments,
  MAX_BOOKING_NOTIFICATIONS,
  ORCHESTRATOR_REFRESH_INTERVAL_MS,
  playNewBookingChime,
  prependOrchestratorNotifications,
  reconcileAppointmentIntoAdminCaches,
  setupOrchestratorChimeUnlock,
  type OrchestratorBookingNotification,
} from '../utils/orchestrator-mode';

type AdminDataContextValue = {
  currentEmployeeQuery: UseQueryResult<Employee>;
  employeesQuery: UseQueryResult<Employee[]>;
  hydrateAppointmentsQuery: UseQueryResult<boolean>;
  scheduleQuery: UseQueryResult<Map<string, string>>;
  workHoursQuery: UseQueryResult<WorkHours>;
  appointmentIds: string[];
  myAppointments: Appointment[];
  refreshAppointmentInCache: (appointmentId: string) => Promise<Appointment | null>;
  refreshAppointmentInCacheWithStatus: (appointmentId: string) => Promise<RefetchAppointmentResult>;
  bookingNotifications: BookingNotification[];
  unreadBookingNotificationCount: number;
  markBookingNotificationsRead: () => void;
};

export type BookingNotification = OrchestratorBookingNotification;

const AdminDataContext = createContext<AdminDataContextValue | null>(null);

const CACHE_STALE_TIME = 1000 * 60 * 20;
const ORCHESTRATOR_HYDRATION_RETRY_MS = 15_000;
export const AdminDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Orchestrator mode uses browser audio alerts. Browsers require a user interaction before allowing playback.
  // We keep this flag in a ref so timer-driven checks can read it without extra renders.
  const chimePlaybackUnlockedRef = useRef(false);
  useEffect(() => {
    return setupOrchestratorChimeUnlock(() => {
      chimePlaybackUnlockedRef.current = true;
    });
  }, []);

  // Business config controls whether orchestrator-only polling + notifications are active.
  const businessConfigQuery = useQuery({
    queryKey: ['businessConfig'],
    queryFn: () => getCurrentBusinessConfig(),
  });
  const isOrchestratorMode = businessConfigQuery.data?.preferences?.orchestratorMode === true;

  const currentEmployeeQuery = useQuery({
    queryKey: ['currentEmployee'],
    queryFn: getCurrentEmployee,
  });

  const employeesQuery = useQuery<Employee[]>({
    queryKey: ['employees'],
    queryFn: () => fetchEmployees(),
    staleTime: CACHE_STALE_TIME,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    // Poll only in orchestrator mode so booking-routing UIs stay current.
    refetchInterval: isOrchestratorMode ? ORCHESTRATOR_REFRESH_INTERVAL_MS : false,
    refetchIntervalInBackground: true,
  });

  const employeeId = currentEmployeeQuery.data?.id;
  const employeeAppointments = currentEmployeeQuery.data?.appointments ?? null;
  const appointmentIds = useMemo(() => {
    if (!employeeAppointments) {
      return [];
    }
    const fourWeeksAgo = dayjs.tz().startOf('day').subtract(4, 'week');
    const uniqueIds = new Set<string>();
    Object.entries(employeeAppointments).forEach(([dateKey, ids]) => {
      if (!Array.isArray(ids)) {
        return;
      }
      // Skip IDs older than four weeks so we only hydrate the recent schedule window
      const appointmentDate = dayjs(dateKey, 'MMDDYYYY');
      if (!appointmentDate.isValid() || appointmentDate.isBefore(fourWeeksAgo)) {
        return;
      }
      ids.forEach((id) => {
        if (typeof id === 'string' && id.trim().length > 0) {
          uniqueIds.add(id);
        }
      });
    });
    return Array.from(uniqueIds).sort();
  }, [employeeAppointments]);

  const queryClient = useQueryClient();
  const [appointmentCacheVersion, setAppointmentCacheVersion] = useState(0);
  // Orchestrator bell feed in AdminDashboard.
  const [bookingNotifications, setBookingNotifications] = useState<BookingNotification[]>([]);
  const unreadBookingNotificationCount = useMemo(
    () => bookingNotifications.reduce((count, notification) => (notification.read ? count : count + 1), 0),
    [bookingNotifications]
  );
  const markBookingNotificationsRead = useCallback(() => {
    setBookingNotifications((previous) => {
      let hasUnread = false;
      const next = previous.map((notification) => {
        if (notification.read) {
          return notification;
        }
        hasUnread = true;
        return { ...notification, read: true };
      });
      return hasUnread ? next : previous;
    });
  }, []);

  const appointmentIdsKey = useMemo(
    () => appointmentIds.slice().sort().join('|'),
    [appointmentIds]
  );

  useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      const key = event?.query?.queryKey;
      if (Array.isArray(key) && key[0] === 'appointment') {
        setAppointmentCacheVersion((version) => version + 1);
      }
    });
    return unsubscribe;
  }, [queryClient]);


  const hydrateAppointmentsQuery = useQuery({
    queryKey: ['hydrateAppointments', WEBSITE_ID, employeeId, appointmentIdsKey],
    enabled: Boolean(employeeId) && appointmentIds.length > 0,
    queryFn: async () => {
      console.log(
        `[AdminDataContext] hydrateAppointments query fired for employee ${employeeId} with ${appointmentIds.length} appointment id(s)`
      );
      await hydrateAppointmentsIntoCache(queryClient, appointmentIds, WEBSITE_ID);
      return true;
    },
    staleTime: 1000 * 60 * 10,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });


  const myAppointments = useMemo<Appointment[]>(() => {
    return appointmentIds
      .map((id) => queryClient.getQueryData(appointmentKey(WEBSITE_ID, id)) as Appointment | undefined)
      .filter((a): a is Appointment => a !== undefined);
  }, [appointmentIdsKey, appointmentCacheVersion]);

  const refreshAppointmentInCache = useCallback(
    async (appointmentId: string) => {
      try {
        const refreshedAppointment = await refetchAppointmentIntoCache(queryClient, appointmentId, WEBSITE_ID);
        if (!refreshedAppointment) {
          return null;
        }

        // Keep admin calendar/index caches coherent when we fetch a single appointment by route/notification.
        reconcileAppointmentIntoAdminCaches(queryClient, refreshedAppointment, WEBSITE_ID);

        return refreshedAppointment;
      } catch (error) {
        console.error('Failed to refresh appointment cache', error);
        return null;
      }
    },
    [queryClient]
  );

  const refreshAppointmentInCacheWithStatus = useCallback(
    async (appointmentId: string): Promise<RefetchAppointmentResult> => {
      const refreshed = await refetchAppointmentIntoCacheWithStatus(queryClient, appointmentId, WEBSITE_ID);
      if (refreshed.status === 'ok') {
        // Keep admin calendar/index caches coherent when we fetch a single appointment by route/notification.
        reconcileAppointmentIntoAdminCaches(queryClient, refreshed.appointment, WEBSITE_ID);
      }
      return refreshed;
    },
    [queryClient]
  );

  const orchestratorHydrationStartedRef = useRef(false);
  const orchestratorHydrationRunningRef = useRef(false);
  const orchestratorKnownAppointmentIdsRef = useRef<Set<string>>(new Set());
  const orchestratorHydrationRetryTimeoutRef = useRef<number | null>(null);
  const [orchestratorHydrationRetryTick, setOrchestratorHydrationRetryTick] = useState(0);
  useEffect(() => {
    return () => {
      if (orchestratorHydrationRetryTimeoutRef.current != null) {
        window.clearTimeout(orchestratorHydrationRetryTimeoutRef.current);
        orchestratorHydrationRetryTimeoutRef.current = null;
      }
    };
  }, []);
  useEffect(() => {
    // Entire block below is orchestrator-only:
    // 1) compare employee appointment indexes against local cache
    // 2) hydrate only missing appointment docs
    // 3) detect newly observed IDs to drive notifications/chime
    if (!isOrchestratorMode || !employeesQuery.data) {
      orchestratorHydrationStartedRef.current = false;
      orchestratorKnownAppointmentIdsRef.current = new Set();
      if (orchestratorHydrationRetryTimeoutRef.current != null) {
        window.clearTimeout(orchestratorHydrationRetryTimeoutRef.current);
        orchestratorHydrationRetryTimeoutRef.current = null;
      }
      return;
    }
    if (orchestratorHydrationRunningRef.current) {
      return;
    }

    const { latestAppointmentIds, missingAppointmentIds } = findMissingOrchestratorAppointmentIds(
      employeesQuery.data,
      queryClient,
      WEBSITE_ID
    );
    if (latestAppointmentIds.length === 0) {
      orchestratorHydrationStartedRef.current = true;
      return;
    }

    orchestratorHydrationRunningRef.current = true;
    const shouldChime = orchestratorHydrationStartedRef.current;
    // Initial load establishes baseline quietly; we only chime after baseline has completed once.
    const newlyObservedAppointmentIds = shouldChime
      ? latestAppointmentIds.filter((id) => !orchestratorKnownAppointmentIdsRef.current.has(id))
      : [];
    let hydrationRunSucceeded = false;

    void hydrateMissingOrchestratorAppointments(queryClient, missingAppointmentIds, WEBSITE_ID)
      .then(async ({ hydrated }) => {
        const appointmentsForNotification = filterNotifiableOrchestratorAppointments(
          getCachedAppointmentsByIds(
            queryClient,
            newlyObservedAppointmentIds,
            WEBSITE_ID
          )
        );
        console.log(
          `[Orchestrator Refresh] Evaluated ${latestAppointmentIds.length} IDs, missing ${missingAppointmentIds.length}, hydrated ${hydrated}, shouldChime=${shouldChime}, newIds=${newlyObservedAppointmentIds.length}.`
        );
        if (shouldChime && appointmentsForNotification.length > 0) {
          const detectedAtMs = Date.now();
          setBookingNotifications((previous) =>
            prependOrchestratorNotifications(previous, appointmentsForNotification, detectedAtMs, MAX_BOOKING_NOTIFICATIONS)
          );
          console.log('[Orchestrator Refresh] New appointments detected. Playing chime.');
          await playNewBookingChime(chimePlaybackUnlockedRef.current);
        } else if (!shouldChime) {
          console.log('[Orchestrator Refresh] Initial hydrate pass completed (chime suppressed).');
        } else {
          console.log('[Orchestrator Refresh] No newly observed customer appointments. No chime played.');
        }
        hydrationRunSucceeded = true;
      })
      .catch((error) => {
        console.error('Failed to hydrate new orchestrator appointments', error);
        if (orchestratorHydrationRetryTimeoutRef.current == null) {
          orchestratorHydrationRetryTimeoutRef.current = window.setTimeout(() => {
            orchestratorHydrationRetryTimeoutRef.current = null;
            setOrchestratorHydrationRetryTick((value) => value + 1);
          }, ORCHESTRATOR_HYDRATION_RETRY_MS);
        }
      })
      .finally(() => {
        if (hydrationRunSucceeded) {
          if (orchestratorHydrationRetryTimeoutRef.current != null) {
            window.clearTimeout(orchestratorHydrationRetryTimeoutRef.current);
            orchestratorHydrationRetryTimeoutRef.current = null;
          }
          // Keep previously acknowledged IDs that are still in the latest index (to avoid
          // duplicate re-alerts during transient cache misses), and only add newly
          // acknowledged IDs once they are actually present in cache.
          const latestAppointmentIdsSet = new Set(latestAppointmentIds);
          const nextKnownAppointmentIds = new Set(
            Array.from(orchestratorKnownAppointmentIdsRef.current).filter((id) => latestAppointmentIdsSet.has(id))
          );
          latestAppointmentIds.forEach((id) => {
            if (queryClient.getQueryData(appointmentKey(WEBSITE_ID, id)) != null) {
              nextKnownAppointmentIds.add(id);
            }
          });
          orchestratorKnownAppointmentIdsRef.current = nextKnownAppointmentIds;
          orchestratorHydrationStartedRef.current = true;
        }
        orchestratorHydrationRunningRef.current = false;
      });
  }, [isOrchestratorMode, employeesQuery.data, orchestratorHydrationRetryTick, queryClient]);


  const scheduleQuery = useQuery<Map<string, string>>({
    queryKey: ['employeeSchedule', employeeId],
    queryFn: () => fetchEmployeeSchedule(employeeId!),
    enabled: !!employeeId,
    staleTime: CACHE_STALE_TIME,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const workHoursQuery = useQuery<WorkHours>({
    queryKey: ['employeeWorkHours', employeeId],
    queryFn: () => getEmployeeWorkHours(employeeId!),
    enabled: !!employeeId,
    staleTime: CACHE_STALE_TIME,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const value = useMemo(
    () => ({
      currentEmployeeQuery,
      employeesQuery,
      hydrateAppointmentsQuery,
      scheduleQuery,
      workHoursQuery,
      appointmentIds,
      myAppointments,
      refreshAppointmentInCache,
      refreshAppointmentInCacheWithStatus,
      bookingNotifications,
      unreadBookingNotificationCount,
      markBookingNotificationsRead,
    }),
    [
      currentEmployeeQuery,
      employeesQuery,
      hydrateAppointmentsQuery,
      scheduleQuery,
      workHoursQuery,
      appointmentIds,
      myAppointments,
      refreshAppointmentInCache,
      refreshAppointmentInCacheWithStatus,
      bookingNotifications,
      unreadBookingNotificationCount,
      markBookingNotificationsRead,
    ]
  );


  return <AdminDataContext.Provider value={value}>{children}</AdminDataContext.Provider>;
};

export const useAdminData = (): AdminDataContextValue => {
  const context = useContext(AdminDataContext);
  if (!context) {
    throw new Error('useAdminData must be used within an AdminDataProvider');
  }
  return context;
};
